/* eslint-disable @typescript-eslint/no-explicit-any */
// AIAssistant uses dynamic message parsing from web worker with varied formats
// that cannot be statically typed. Using 'any' is appropriate for this use case.
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  MicrophoneIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { Folder } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { DashedTag } from '@/components/chariot-ui';
import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { useAIAgents } from '@/hooks/useAIAgents';
import {
  useDeleteConversation,
  useGetChildConversations,
  useGetConversationById,
  useGetConversationTraces,
} from '@/hooks/useChat';
import { useChatWorker } from '@/hooks/useChatWorker';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useFlag } from '@/hooks/useFlags';
import { useAuth } from '@/state/auth';
import {
  getTenantScopedValue,
  removeTenantScopedValue,
  setTenantScopedValue,
} from '@/utils/storage/tenantScoping.util';

import ChatGuide from './ChatGuide';
import ChatMessage from './ChatMessage';
import ChatWelcome from './ChatWelcome';
import { AgentExecutionCard } from './components/AgentExecutionCard';
import TipsMenu from './components/TipsMenu';
import { TraceDrawer } from './components/TraceDrawer';
import ConversationList from './ConversationList';
import { useTraceDrawerAutoOpen } from './hooks/useTraceDrawerAutoOpen';
import ModeIndicator from './ModeToggle';
import { useTraceDrawerStore } from './stores/traceDrawer.store';
import { useCommandMenu } from './useCommandMenu';

export const AIAssistant: React.FC = () => {
  const LAST_ACTIVE_CONVERSATION_KEY = 'ai-assistant.lastActiveConversationId';

  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get('conversationId');
  const summonMessage = searchParams.get('summonMessage');
  const summonMode = searchParams.get('summonMode') as 'query' | 'agent' | null;
  const [newMessage, setNewMessage] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>('');
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [activeTab, setActiveTab] = useState<'conversations' | 'guide'>(
    'conversations'
  );
  const [mode, setMode] = useState<'query' | 'agent'>('query');
  const [activeAgent, setActiveAgent] = useState<string | null>(null);

  interface AgentExecution {
    agentName: string;
    agentTitle: string;
    status: 'running' | 'completed' | 'error';
    toolCount: number;
    tokenCount: number;
    toolCalls: Array<{ name: string; status: 'pending' | 'done' | 'error' }>;
  }

  const [agentExecutions, setAgentExecutions] = useState<
    Map<string, AgentExecution>
  >(new Map());
  const [expandedAgentCards, setExpandedAgentCards] = useState<Set<string>>(
    new Set()
  );
  const [deletedConversationIds, setDeletedConversationIds] = useState<
    Set<string>
  >(new Set());

  const deleteConversation = useDeleteConversation();

  const handleDeleteConversation = (id: string) => {
    // Optimistically hide it from the list immediately
    setDeletedConversationIds(prev => new Set(prev).add(id));

    deleteConversation.mutate(id, {
      onSuccess: () => {
        if (conversationId === id) {
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('conversationId');
            return newParams;
          });
        }
      },
      onError: () => {
        // Rollback if the API call fails
        setDeletedConversationIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
    });
  };

  const toggleAgentCard = (conversationId: string) => {
    setExpandedAgentCards(prev => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  };

  // Helper functions for per-conversation mode storage
  const getConversationModes = (): Record<
    string,
    { mode: 'query' | 'agent'; agent?: string }
  > => {
    try {
      const saved = localStorage.getItem('ai-assistant-conversation-modes');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const saveConversationMode = (
    convId: string,
    convMode: 'query' | 'agent',
    agentName?: string
  ) => {
    const modes = getConversationModes();
    modes[convId] = {
      mode: convMode,
      agent: agentName,
    };

    // Limit storage to prevent unbounded growth (keep most recent 100)
    const keys = Object.keys(modes);
    if (keys.length > 100) {
      keys.slice(0, keys.length - 100).forEach(k => delete modes[k]);
    }

    localStorage.setItem(
      'ai-assistant-conversation-modes',
      JSON.stringify(modes)
    );
  };

  const getConversationMode = (
    convId: string
  ): { mode: 'query' | 'agent'; agent?: string } => {
    const modes = getConversationModes();
    return modes[convId] || { mode: 'query' };
  };
  const [showTracesPopover, setShowTracesPopover] = useState(false);
  const [showToolCallsPopover, setShowToolCallsPopover] = useState(false);
  const [previousConversationId, setPreviousConversationId] = useState<
    string | null
  >(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [savedMessage, setSavedMessage] = useState('');
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [thinkingStartTime, setThinkingStartTime] = useState<number | null>(
    null
  );
  const [isRecording, setIsRecording] = useState(false);
  const [showTipsMenu, setShowTipsMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const commandMenuRef = useRef<HTMLDivElement>(null);
  const summonProcessedRef = useRef(false);

  const { isImpersonating, friend, me, isPraetorianUser, whoami } = useAuth();
  const {
    locked: aiAssistantLocked,
    lockTitle,
    lockDescription,
    featureName,
    isLoading: featureLockLoading,
  } = useFeatureLock('aiAssistant');
  const { disableAIInference, enableAIAgents } = useFlag();

  // When AI inference is disabled but user bypasses the lock (e.g., Praetorian users),
  // show a warning banner and disable inputs instead of silently failing
  const isAIDisabledButBypassed = disableAIInference && !aiAssistantLocked;
  const { data: agents } = useAIAgents();

  // Match backend authorization: Praetorian users with analyst+ role always have access;
  // non-Praetorian users need the enable_ai_agents flag AND analyst+ role.
  const isAtLeastAnalyst =
    whoami?.role === 'analyst' || whoami?.role === 'admin';
  const canUseAgentMode =
    (isPraetorianUser && isAtLeastAnalyst) ||
    (enableAIAgents && isAtLeastAnalyst);

  const handleEntitySearch = async (
    type: 'asset' | 'risk' | 'webpage' | 'capability',
    query: string
  ) => {
    if (!query) return;

    const searchQuery =
      type === 'asset'
        ? `Show me assets where name or dns contains "${query}"`
        : type === 'risk'
          ? `Show me risks where name or title contains "${query}"`
          : type === 'webpage'
            ? `Show me webpages where url contains "${query}"`
            : `Tell me about the ${query} capability`;

    // Send as a regular message
    if (conversationId) {
      await sendMessageWorker(conversationId, searchQuery, mode);
    } else {
      // Create conversation with search query
      const newConversation = await createConversationWorker(searchQuery, mode);
      const newId =
        newConversation.uuid ||
        newConversation.id ||
        newConversation.key?.replace('#conversation#', '');
      if (newId) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('conversationId', newId);
          return newParams;
        });
      }
    }
  };

  const {
    showCommandMenu,
    filteredCommands,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    replaceCurrentWord,
    menuType,
  } = useCommandMenu(
    newMessage,
    canUseAgentMode,
    setMode,
    () => setNewMessage(''),
    handleEntitySearch,
    agents,
    mode
  );

  // Track user context to clear conversation on account switch
  const currentUser = isImpersonating ? friend : me;
  const previousUser = useRef<string>('');

  // Clear conversation when switching accounts
  useEffect(() => {
    if (
      currentUser &&
      previousUser.current &&
      previousUser.current !== currentUser
    ) {
      // User switched - clear conversation from URL and reset any pending states
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('conversationId');
        return newParams;
      });

      // Also clear any local state that might be holding onto the old conversation
      setFailedMessage(null);
      setIsWaitingForResponse(false);
    }
    previousUser.current = currentUser;
  }, [currentUser, setSearchParams]);

  // When AI inference is disabled, clear any active conversation to prevent
  // getting stuck in a "Loading conversation..." loop that never resolves
  useEffect(() => {
    if (isAIDisabledButBypassed && conversationId) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('conversationId');
          return newParams;
        },
        { replace: true }
      );
      setIsLoadingConversation(false);
      removeTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY);
    }
  }, [isAIDisabledButBypassed, conversationId, setSearchParams]);

  // Restore last active conversation from tenant-scoped localStorage on mount
  useEffect(() => {
    if (!conversationId && !summonMessage && !disableAIInference) {
      const lastConversationId = getTenantScopedValue<string>(
        LAST_ACTIVE_CONVERSATION_KEY
      );
      const UUID_REGEX =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (lastConversationId && UUID_REGEX.test(lastConversationId)) {
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', lastConversationId);
            return newParams;
          },
          { replace: true }
        );
      }
    }
  }, []); // Mount-only: reads URL params once to restore session. Post-mount flag changes handled by isAIDisabledButBypassed effect above.

  // Use chat worker for conversations and messages
  const {
    conversations,
    messages,
    pendingMessages: workerPendingMessages,
    sendMessage: sendMessageWorker,
    createConversation: createConversationWorkerRaw,
  } = useChatWorker(conversationId || undefined);

  // Filter out optimistically deleted conversations
  const visibleConversations = useMemo(
    () =>
      conversations.filter((c: any) => {
        const id =
          c.uuid || c.id || (c.key ? c.key.replace('#conversation#', '') : '');
        return !deletedConversationIds.has(id);
      }),
    [conversations, deletedConversationIds]
  );

  // Pre-compute lightweight preview map for ConversationList (prevents DataCloneError)
  const conversationPreviews = useMemo(() => {
    const previews: Record<string, string> = {};
    for (const conv of conversations) {
      const id =
        conv.uuid || conv.id || conv.key?.replace('#conversation#', '') || '';
      // Already has a title from the backend? Skip message scanning.
      if (conv.name || conv.title || conv.topic) continue;

      // Find first user message for this conversation
      const firstUserMsg = messages.find(
        (msg: any) =>
          msg.role === 'user' &&
          (msg.key?.includes(id) || msg.conversationId === id)
      );
      if (firstUserMsg?.content) {
        const words = firstUserMsg.content
          .trim()
          .split(' ')
          .slice(0, 6)
          .join(' ');
        previews[id] =
          words.length > 40 ? words.substring(0, 40) + '...' : words;
      }
    }
    return previews;
  }, [conversations, messages]);

  // Wrapper that saves mode when creating a conversation
  const createConversationWorker = async (
    message: string,
    convMode: 'query' | 'agent'
  ) => {
    const newConversation = await createConversationWorkerRaw(
      message,
      convMode
    );
    const newId =
      newConversation.uuid ||
      newConversation.id ||
      newConversation.key?.replace('#conversation#', '');
    if (newId) {
      saveConversationMode(newId, convMode);
    }
    return newConversation;
  };

  const { data: selectedConversationById } = useGetConversationById(
    conversationId || undefined
  );

  const selectedConversation = conversationId
    ? conversations.find(
        c =>
          c.uuid === conversationId ||
          c.id === conversationId ||
          c.key?.includes(conversationId)
      ) || selectedConversationById
    : undefined;

  // Get child (subagent) conversations — anchor to parent when viewing a child
  // parent_id is "self" for root conversations, or a UUID for subagent conversations
  const parentConversationId = selectedConversation?.parent_id;
  const isSubagent = !!parentConversationId && parentConversationId !== 'self';
  const subagentStatus = selectedConversation?.status;

  // Disable input for subagents that are queued (not ready yet) or completed.
  const isSubagentQueued = isSubagent && subagentStatus === 'queued';
  const isSubagentFinished = isSubagent && subagentStatus === 'idle';
  const isInputDisabled =
    aiAssistantLocked ||
    isAIDisabledButBypassed ||
    isSubagentQueued ||
    isSubagentFinished;
  const subagentAnchorId = isSubagent
    ? parentConversationId
    : conversationId || undefined;
  const { data: childConversations } =
    useGetChildConversations(subagentAnchorId);

  const { data: traces } = useGetConversationTraces(
    selectedConversation?.trace_id
  );

  // Auto-open trace drawer when workflow starts
  useTraceDrawerAutoOpen({
    traceId: selectedConversation?.trace_id,
    traces,
    enabled: !!conversationId,
  });

  const { open: openTraceDrawer, close: closeTraceDrawer } =
    useTraceDrawerStore();

  // Close trace drawer when navigating away from AI Assistant
  useEffect(() => {
    return () => {
      closeTraceDrawer();
    };
  }, [closeTraceDrawer]);

  // Track conversation switching and clear states to prevent flash
  useEffect(() => {
    if (conversationId !== previousConversationId) {
      if (conversationId) {
        setIsLoadingConversation(true);
        // Restore mode and agent for this specific conversation
        const savedState = getConversationMode(conversationId);
        setMode(
          savedState.mode === 'agent' && !canUseAgentMode
            ? 'query'
            : savedState.mode
        );
        setActiveAgent(savedState.agent || null);
      } else {
        // When clearing conversation (new chat), reset to query mode
        setMode('query');
        setActiveAgent(null);
      }
      // Clear any pending states to prevent content flash when switching conversations
      setFailedMessage(null);
      setIsWaitingForResponse(false);
      // Reset history navigation state when switching conversations
      setMessageHistory([]);
      setHistoryIndex(-1);
      setSavedMessage('');
    }
    setPreviousConversationId(conversationId);
  }, [conversationId, previousConversationId, canUseAgentMode]);

  // Save mode and active agent to localStorage for current conversation whenever they change
  useEffect(() => {
    if (conversationId) {
      saveConversationMode(conversationId, mode, activeAgent || undefined);
    }
  }, [mode, activeAgent, conversationId]);

  // Reset to query mode if agent mode becomes unavailable (e.g., flag toggled off)
  useEffect(() => {
    if (!canUseAgentMode && mode === 'agent') {
      setMode('query');
      setActiveAgent(null);
    }
  }, [canUseAgentMode, mode]);

  // Persist active conversation to tenant-scoped localStorage for session restore
  useEffect(() => {
    if (conversationId) {
      setTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY, conversationId);
    } else {
      removeTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY);
    }
  }, [conversationId]);

  // Handle summon parameters from URL (Ask Marcus AI)
  useEffect(() => {
    if (
      !summonMessage ||
      !summonMode ||
      summonProcessedRef.current ||
      isAIDisabledButBypassed
    )
      return;
    summonProcessedRef.current = true;

    // Clear summon params from URL immediately to prevent re-triggering
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('summonMessage');
        newParams.delete('summonMode');
        return newParams;
      },
      { replace: true }
    );

    // Set mode to agent for Aurelius
    setMode('agent');

    // Create conversation and send the summon message
    const initSummon = async () => {
      try {
        const newConversation = await createConversationWorker(
          summonMessage,
          'agent'
        );
        const newId =
          newConversation.uuid ||
          newConversation.id ||
          newConversation.key?.replace('#conversation#', '');
        if (newId) {
          saveConversationMode(newId, 'agent');
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', newId);
            return newParams;
          });
        }
      } catch (error) {
        console.error('Failed to create summon conversation:', error);
        setFailedMessage(summonMessage);
      }
    };

    initSummon();
  }, [summonMessage, summonMode]);

  // Stop loading when messages arrive or when no conversation is selected
  // Also populate message history from loaded conversation
  useEffect(() => {
    if ((messages.length > 0 || !conversationId) && isLoadingConversation) {
      setIsLoadingConversation(false);
      if (messages.length > 0) {
        const userMessages = messages
          .filter(msg => msg.role === 'user' && msg.content)
          .map(msg => msg.content);
        if (userMessages.length > 0) {
          setMessageHistory(userMessages);
          setHistoryIndex(-1);
          setSavedMessage('');
        }
      }
    }
  }, [messages.length, conversationId, isLoadingConversation]);

  // Auto-scroll to ensure we're always at the very bottom (debounced)
  useEffect(() => {
    // Scroll when messages or pending messages change
    if (
      (messages.length > 0 || workerPendingMessages.length > 0) &&
      scrollContainerRef.current
    ) {
      // Use requestAnimationFrame to batch scroll updates
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      });
    }
  }, [messages.length, workerPendingMessages.length, conversationId]);

  // Track agent execution when response arrives
  useEffect(() => {
    if (conversationId && activeAgent && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const isThinking =
        lastMessage &&
        lastMessage.role !== 'chariot' &&
        lastMessage.role !== 'planner-output';

      // Get agent title from agent list
      const agentData = agents?.find(
        (a: { name: string }) => a.name === activeAgent
      );
      const agentTitle = agentData?.title || activeAgent;

      // Count tool calls
      const toolCalls = messages.filter(msg => msg.role === 'tool call');

      const execution: AgentExecution = {
        agentName: activeAgent,
        agentTitle: agentTitle,
        status: isThinking ? 'running' : 'completed',
        toolCount: toolCalls.length,
        tokenCount: 0, // TODO: Get actual token count from backend
        toolCalls: toolCalls.map((tc: any) => {
          try {
            const toolData = JSON.parse(tc.toolUseContent || tc.content);
            return {
              name: toolData?.Name || toolData?.tool || 'tool',
              status: 'done' as const,
            };
          } catch {
            return { name: 'tool', status: 'done' as const };
          }
        }),
      };

      setAgentExecutions(prev => new Map(prev).set(conversationId, execution));
    }
  }, [messages, activeAgent, conversationId, agents]);

  // Update loading status based on actual backend roles and activity
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const isThinking =
      lastMessage &&
      lastMessage.role !== 'chariot' &&
      lastMessage.role !== 'planner-output';

    if (!isThinking) {
      setLoadingStatus('');
      setThinkingStartTime(null);
      return;
    }

    // Start timing when thinking begins
    if (isThinking && !thinkingStartTime) {
      setThinkingStartTime(Date.now());
    }

    const recentMessages = messages.slice(-10);
    const lastToolCall = recentMessages
      .filter(msg => msg.role === 'tool call')
      .pop();
    const lastToolResponse = recentMessages
      .filter(msg => msg.role === 'tool response')
      .pop();

    const isExecuting =
      lastToolCall &&
      (!lastToolResponse ||
        new Date(lastToolCall.timestamp || '').getTime() >
          new Date(lastToolResponse.timestamp || '').getTime());

    if (isExecuting && lastToolCall) {
      try {
        const toolData = JSON.parse(
          (lastToolCall as any).toolUseContent || lastToolCall.content
        );
        const toolName = toolData?.Name || toolData?.tool || 'tool';

        // Provide more specific status based on tool type
        if (toolName === 'query') {
          const input = toolData?.Input || toolData?.input;
          if (input?.node?.labels) {
            setLoadingStatus(`Querying ${input.node.labels.join(', ')}...`);
            return;
          }
          setLoadingStatus('Querying database...');
          return;
        }

        if (toolName === 'job') {
          const input = toolData?.Input || toolData?.input;
          const capability = input?.capability || 'scan';
          setLoadingStatus(`Running ${capability}...`);
          return;
        }

        if (toolName === 'schema') {
          setLoadingStatus('Fetching schema information...');
          return;
        }

        setLoadingStatus(`Executing ${toolName}...`);
      } catch {
        setLoadingStatus('Executing tool...');
      }
    } else if (lastToolResponse) {
      // Check if we have results to analyze
      try {
        const responseData = JSON.parse(lastToolResponse.content);
        if (responseData.count !== undefined && responseData.count > 0) {
          setLoadingStatus(`Analyzing ${responseData.count} results...`);
          return;
        }
        if (responseData.error) {
          setLoadingStatus('Handling error...');
          return;
        }
      } catch {
        // Not JSON
      }
      setLoadingStatus('Analyzing results...');
    } else {
      setLoadingStatus('Thinking...');
    }
  }, [messages, thinkingStartTime]);

  // Safety: Stop loading after 60 seconds max to prevent stuck loading states
  useEffect(() => {
    if (isWaitingForResponse) {
      const timeout = setTimeout(() => {
        setIsWaitingForResponse(false);
      }, 60000);
      return () => clearTimeout(timeout);
    }
  }, [isWaitingForResponse]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [newMessage]);

  // Auto-focus on textarea when conversation loads or changes
  useEffect(() => {
    if (textareaRef.current && conversationId && !isInputDisabled) {
      textareaRef.current.focus();
    }
  }, [conversationId, isInputDisabled]);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;
          setNewMessage(prev => (prev ? prev + ' ' + transcript : transcript));
          setIsRecording(false);
        };

        recognitionRef.current.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsRecording(false);
          // Don't show alert for 'no-speech' - just silently stop
          if (event.error !== 'no-speech' && event.error !== 'aborted') {
            console.warn('Speech recognition failed:', event.error);
          }
        };

        recognitionRef.current.onend = () => {
          setIsRecording(false);
        };
      }
    }
  }, []);

  // Detect scroll position to show/hide scroll-to-bottom button (throttled)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const { scrollTop, scrollHeight, clientHeight } = container;
          const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
          setShowScrollToBottom(!isNearBottom);
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCreateConversation = () => {
    if (isInputDisabled) return;

    // Simple: just clear conversation ID to show blank chat state
    setIsWaitingForResponse(false);
    setMode('query'); // Reset to query mode for new conversations
    setActiveAgent(null); // Clear active agent
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('conversationId');
      return newParams;
    });
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  const handleTracesCommand = () => {
    if (selectedConversation?.trace_id) {
      openTraceDrawer(selectedConversation.trace_id);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (
        showTracesPopover &&
        !target.closest('.traces-container') &&
        !target.closest('.traces-button')
      ) {
        setShowTracesPopover(false);
      }
      if (
        showToolCallsPopover &&
        !target.closest('.tool-calls-container') &&
        !target.closest('.tool-calls-button')
      ) {
        setShowToolCallsPopover(false);
      }
      if (
        showTipsMenu &&
        !target.closest('.tips-menu-container') &&
        !target.closest('.tips-menu-button')
      ) {
        setShowTipsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTracesPopover, showToolCallsPopover, showTipsMenu]);

  const handleExampleClick = async (
    query: string,
    sendImmediately: boolean = true
  ) => {
    if (isInputDisabled) return;

    // If not sending immediately, just populate the input
    if (!sendImmediately) {
      setNewMessage(query);
      // Focus input but don't switch tabs
      setTimeout(() => textareaRef.current?.focus(), 100);
      return;
    }

    try {
      let currentConversationId = conversationId;

      if (!currentConversationId) {
        const newConversation = await createConversationWorker(query, mode);
        const newId =
          newConversation.uuid ||
          newConversation.id ||
          newConversation.key?.replace('#conversation#', '');
        if (newId) {
          currentConversationId = newId;
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', newId);
            return newParams;
          });
        }
      }

      if (!currentConversationId) {
        throw new Error('No conversation ID available');
      }

      await sendMessageWorker(currentConversationId, query, mode);
    } catch (error) {
      console.error('Failed to send example message:', query, error);
      setFailedMessage(query);
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert(
        'Speech recognition is not supported in your browser. Please use Chrome, Safari, or Edge.'
      );
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e?.preventDefault?.();
    if (!newMessage.trim() || isInputDisabled) return;

    const messageText = newMessage.trim();

    // Handle @agent mentions (set active agent without sending message)
    if (messageText.startsWith('@') && !messageText.includes(' ')) {
      const agentName = messageText.slice(1).trim();
      setActiveAgent(agentName);
      if (conversationId) {
        saveConversationMode(conversationId, 'agent', agentName);
      }
      setNewMessage('');
      return;
    }

    // Handle /agent:name command - set active agent but don't send as message
    if (messageText.startsWith('/agent:')) {
      const agentName = messageText.slice(7).trim();
      setActiveAgent(agentName);
      if (conversationId) {
        saveConversationMode(conversationId, 'agent', agentName);
      }
      setNewMessage('');
      return;
    }

    // Auto-complete if there's exactly one matching command
    if (shouldAutoComplete) {
      selectCommand(filteredCommands[0].command);
      return;
    }

    // Check if it's a command
    if (executeCommandIfMatch(messageText)) {
      return;
    }

    // Add to message history
    setMessageHistory(prev => [...prev, messageText]);
    setHistoryIndex(-1); // Reset history index
    setSavedMessage(''); // Clear saved message on send
    setNewMessage(''); // Clear input immediately to allow rapid sending

    try {
      let currentConversationId = conversationId;

      if (!currentConversationId) {
        const newConversation = await createConversationWorker(
          messageText,
          (activeAgent || mode) as 'query' | 'agent'
        );
        const newId =
          newConversation.uuid ||
          newConversation.id ||
          newConversation.key?.replace('#conversation#', '');
        if (newId) {
          currentConversationId = newId;
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', newId);
            return newParams;
          });
        }
      }

      if (!currentConversationId) {
        throw new Error('No conversation ID available');
      }

      await sendMessageWorker(
        currentConversationId,
        messageText,
        (activeAgent || mode) as 'query' | 'agent'
      );
    } catch (error) {
      console.error('Failed to send message:', messageText, error);
      setFailedMessage(messageText);
    }
  };

  // Show loading state while feature lock is evaluating
  if (featureLockLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader isLoading type="spinner" />
      </div>
    );
  }

  // Don't show full-screen loader for chat - we have our own loading states

  return (
    <div className="relative flex h-full min-w-0 overflow-hidden">
      <FeatureLock
        locked={aiAssistantLocked}
        title={lockTitle}
        description={lockDescription}
        action={createContactSupportButton(featureName)}
      >
        {/* Sidebar */}
        <div
          className={`border-chariot-stroke relative flex flex-col border-r transition-all duration-300 ${
            isSidebarCollapsed ? 'w-16' : 'w-72'
          }`}
        >
          <div className="absolute top-4 -right-3 z-10">
            <button
              onClick={toggleSidebar}
              disabled={isAIDisabledButBypassed}
              className={`border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary flex size-6 items-center justify-center rounded-full border shadow-xs transition-colors ${isAIDisabledButBypassed ? 'text-chariot-stroke cursor-not-allowed' : 'hover:text-chariot-text-primary'}`}
            >
              {isSidebarCollapsed ? (
                <ChevronRightIcon className="size-3" />
              ) : (
                <ChevronLeftIcon className="size-3" />
              )}
            </button>
          </div>

          {isSidebarCollapsed ? (
            <div className="-ml-3 flex h-full flex-col items-center space-y-6 py-6">
              <button
                onClick={handleCreateConversation}
                disabled={isAIDisabledButBypassed}
                className={`flex size-10 items-center justify-center rounded-lg border shadow-xs transition-colors ${isAIDisabledButBypassed ? 'border-chariot-stroke text-chariot-text-secondary cursor-not-allowed opacity-40' : 'border-chariot-brand-primary text-chariot-text-primary hover:bg-chariot-background-secondary'}`}
                title="New Conversation"
              >
                <PlusIcon className="size-5" />
              </button>
              <div className="bg-chariot-stroke h-px w-6"></div>
              <button
                onClick={toggleSidebar}
                disabled={isAIDisabledButBypassed}
                className={`text-chariot-text-secondary relative flex size-9 items-center justify-center transition-all ${isAIDisabledButBypassed ? 'cursor-not-allowed opacity-40' : 'hover:text-chariot-text-primary'}`}
                title="Conversations"
              >
                <Folder className="size-5" />
              </button>
              <button
                onClick={() => {
                  if (!isAIDisabledButBypassed) {
                    setActiveTab('guide');
                    setIsSidebarCollapsed(false);
                  }
                }}
                disabled={isAIDisabledButBypassed}
                className={`text-chariot-text-secondary flex size-9 items-center justify-center transition-all ${isAIDisabledButBypassed ? 'cursor-not-allowed opacity-40' : 'hover:text-chariot-text-primary'}`}
                title="Prompt Library"
              >
                <QuestionMarkCircleIcon className="size-5" />
              </button>
            </div>
          ) : (
            <>
              {/* Tab Headers */}
              <div className="border-chariot-stroke flex border-b">
                <button
                  onClick={() => setActiveTab('conversations')}
                  className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'conversations'
                      ? 'text-chariot-brand-primary border-chariot-brand-primary border-b-2'
                      : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                  }`}
                >
                  <Folder className="size-4" />
                  <span>Conversations</span>
                </button>
                <button
                  onClick={() => setActiveTab('guide')}
                  className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'guide'
                      ? 'text-chariot-brand-primary border-chariot-brand-primary border-b-2'
                      : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                  }`}
                >
                  <QuestionMarkCircleIcon className="size-4" />
                  <span>Prompts</span>
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex min-h-0 flex-1 flex-col">
                {activeTab === 'conversations' ? (
                  <>
                    <div className="border-chariot-stroke border-b px-4 py-3">
                      <Button
                        onClick={handleCreateConversation}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full"
                      >
                        <PlusIcon className="size-4" />
                        New Conversation
                      </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-2 py-2">
                      <ConversationList
                        conversations={visibleConversations}
                        activeConversationId={conversationId}
                        conversationPreviews={conversationPreviews}
                        onDeleteConversation={handleDeleteConversation}
                        onSelectConversation={id => {
                          setSearchParams(prev => {
                            const newParams = new URLSearchParams(prev);
                            newParams.set('conversationId', id);
                            return newParams;
                          });
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="border-chariot-stroke border-b px-4 py-3">
                      <Button
                        onClick={handleCreateConversation}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full"
                      >
                        <PlusIcon className="size-4" />
                        New Conversation
                      </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <ChatGuide
                        onPromptClick={query =>
                          handleExampleClick(query, false)
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Main Chat Area */}
        <div className="relative flex min-w-0 flex-1 flex-col">
          {/* Header with Mode and Status - only show when in conversation */}
          {conversationId && (
            <div className="border-chariot-stroke bg-chariot-background-primary border-b">
              <div className="flex h-14 items-center">
                {/* Conversation topic */}
                <div className="border-chariot-stroke flex h-full min-w-0 flex-1 items-center border-r px-6">
                  <span className="text-chariot-text-primary text-md truncate font-semibold">
                    {(() => {
                      // Find current conversation and show topic
                      const currentConversation = conversations.find(
                        c =>
                          c.uuid === conversationId ||
                          c.id === conversationId ||
                          c.key?.includes(conversationId)
                      );

                      if (currentConversation?.topic) {
                        return currentConversation.topic;
                      }

                      // Fall back to first user message
                      const firstUserMessage = messages.find(
                        m => m.role === 'user'
                      );
                      if (firstUserMessage?.content) {
                        return firstUserMessage.content.trim();
                      }

                      return `Conversation ${conversationId.slice(-8)}`;
                    })()}
                  </span>
                </div>

                {/* Tool calls - only show if there are tools */}
                {(() => {
                  const toolCalls = messages.filter(
                    msg => msg.role === 'tool call'
                  );
                  if (toolCalls.length === 0) return null;

                  const hasTraces = traces && traces.length > 0;
                  return (
                    <div
                      className={`text-chariot-text-secondary relative flex h-full items-center px-6 text-xs ${hasTraces ? 'border-chariot-stroke border-l' : ''}`}
                    >
                      <button
                        onClick={() => setShowToolCallsPopover(prev => !prev)}
                        className="hover:text-chariot-text-primary tool-calls-button underline decoration-dotted underline-offset-2"
                      >
                        Tools: {toolCalls.length}
                      </button>

                      {/* Tool calls popover - positioned below button */}
                      {showToolCallsPopover && (
                        <div className="bg-chariot-background-primary border-chariot-stroke tool-calls-container absolute top-full left-1/2 z-50 mt-2 max-h-60 w-96 -translate-x-1/2 overflow-y-auto rounded-lg border shadow-lg">
                          <div className="space-y-1 p-2">
                            {toolCalls.map((toolCall, index) => {
                              const toolData = (() => {
                                try {
                                  return JSON.parse(
                                    (toolCall as any).toolUseContent ||
                                      toolCall.content
                                  );
                                } catch {
                                  return null;
                                }
                              })();
                              const toolName =
                                toolData?.Name || toolData?.tool || 'tool';
                              const input =
                                toolData?.Input ||
                                toolData?.input ||
                                toolData?.params;

                              const toolResponse = messages.find(
                                m =>
                                  m.role === 'tool response' &&
                                  (m as any).toolUseId ===
                                    (toolCall as any).toolUseId
                              );
                              const hasResponse = !!toolResponse;

                              // Check for error first to potentially override description
                              const getResponseSummary = () => {
                                if (!hasResponse || !toolResponse) return null;
                                try {
                                  const responseData = JSON.parse(
                                    toolResponse.content
                                  );
                                  if (responseData.error) {
                                    return {
                                      text: 'error',
                                      isError: true,
                                      errorMessage: responseData.error,
                                    };
                                  }
                                  if (responseData.count !== undefined) {
                                    return {
                                      text: `${responseData.count} results`,
                                      isError: false,
                                    };
                                  }
                                  return { text: 'done', isError: false };
                                } catch {
                                  return { text: 'done', isError: false };
                                }
                              };

                              const responseSummary = getResponseSummary();

                              const getToolDescription = () => {
                                // If there's an error, show the error message
                                if (
                                  responseSummary?.isError &&
                                  responseSummary.errorMessage
                                ) {
                                  return responseSummary.errorMessage;
                                }

                                if (toolName === 'query' && input?.node) {
                                  const labels = input.node.labels || [];
                                  const filters = input.node.filters || [];
                                  const limit = input.limit;

                                  if (labels.length > 0) {
                                    let desc = labels.join(', ');
                                    if (filters.length > 0) {
                                      const filterDesc = filters
                                        .map(
                                          (f: any) =>
                                            `${f.field} ${f.operator} ${Array.isArray(f.value) ? `[${f.value.join(', ')}]` : f.value}`
                                        )
                                        .join(', ');
                                      desc += ` where ${filterDesc}`;
                                    }
                                    if (limit) {
                                      desc += ` (limit ${limit})`;
                                    }
                                    return desc;
                                  }
                                  return 'Query';
                                }

                                if (toolName === 'job' && input?.key) {
                                  const capability = input.capability || 'scan';
                                  const target =
                                    input.key.split('#').pop() || 'target';
                                  return `${capability} on ${target}`;
                                }

                                if (
                                  toolName === 'schema' &&
                                  input?.entity_types
                                ) {
                                  return input.entity_types.join(', ');
                                }

                                // Capitalize first letter of tool name
                                return (
                                  toolName.charAt(0).toUpperCase() +
                                  toolName.slice(1)
                                );
                              };

                              const description = getToolDescription();

                              return (
                                <button
                                  key={toolCall.key || index}
                                  onClick={() => {
                                    const element = document.querySelector(
                                      `[data-message-id="${toolCall.id || toolCall.key}"]`
                                    ) as HTMLElement;
                                    if (element) {
                                      element.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                      });
                                      const button =
                                        element.querySelector('button');
                                      if (button) {
                                        setTimeout(() => button.click(), 300);
                                      }
                                    }
                                    setShowToolCallsPopover(false);
                                  }}
                                  className="hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {/* Status indicator on left */}
                                    <div className="flex w-5 flex-shrink-0 items-center justify-center">
                                      {hasResponse ? (
                                        responseSummary?.isError ? (
                                          <span className="text-sm text-red-500">
                                            ✕
                                          </span>
                                        ) : (
                                          <span className="text-sm text-green-500">
                                            ✓
                                          </span>
                                        )
                                      ) : (
                                        <div className="bg-chariot-text-quaternary size-2 animate-pulse rounded-full"></div>
                                      )}
                                    </div>

                                    {/* Tool name/description in middle */}
                                    <div className="min-w-0 flex-1">
                                      <div className="text-chariot-text-primary truncate text-xs font-medium">
                                        {description}
                                      </div>
                                    </div>

                                    {/* Results count on right */}
                                    <div className="text-chariot-text-secondary flex-shrink-0 text-[10px]">
                                      {responseSummary?.text || 'pending'}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Status */}
                <div className="border-chariot-stroke flex h-full items-center gap-3 border-l px-6">
                  <span className="text-chariot-text-primary text-sm font-medium">
                    Status:
                  </span>
                  <div
                    title={(() => {
                      const lastMessage = messages[messages.length - 1];
                      const isThinking =
                        lastMessage &&
                        lastMessage.role !== 'chariot' &&
                        lastMessage.role !== 'planner-output';

                      if (!isThinking || !thinkingStartTime) {
                        return 'Ready to process queries';
                      }

                      const elapsedSeconds = Math.floor(
                        (Date.now() - thinkingStartTime) / 1000
                      );
                      const minutes = Math.floor(elapsedSeconds / 60);
                      const seconds = elapsedSeconds % 60;
                      const timeStr =
                        minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

                      const recentMessages = messages.slice(-10);
                      const lastToolCall = recentMessages
                        .filter(msg => msg.role === 'tool call')
                        .pop();
                      const lastToolResponse = recentMessages
                        .filter(msg => msg.role === 'tool response')
                        .pop();

                      const isExecuting =
                        lastToolCall &&
                        (!lastToolResponse ||
                          new Date(lastToolCall.timestamp || '').getTime() >
                            new Date(
                              lastToolResponse.timestamp || ''
                            ).getTime());

                      if (isExecuting && lastToolCall) {
                        try {
                          const toolData = JSON.parse(
                            (lastToolCall as any).toolUseContent ||
                              lastToolCall.content
                          );
                          const toolName =
                            toolData?.Name || toolData?.tool || 'tool';
                          const input = toolData?.Input || toolData?.input;

                          if (toolName === 'query' && input?.node?.labels) {
                            return `Querying ${input.node.labels.join(', ')} (${timeStr})`;
                          }
                          if (toolName === 'job') {
                            const capability = input?.capability || 'scan';
                            return `Running ${capability} (${timeStr})`;
                          }
                          return `Executing ${toolName} (${timeStr})`;
                        } catch {
                          return `Executing tool (${timeStr})`;
                        }
                      }

                      if (lastToolResponse) {
                        try {
                          const responseData = JSON.parse(
                            lastToolResponse.content
                          );
                          if (
                            responseData.count !== undefined &&
                            responseData.count > 0
                          ) {
                            return `Analyzing ${responseData.count} results (${timeStr})`;
                          }
                        } catch {
                          // Ignore JSON parse errors
                        }
                      }

                      return `Processing (${timeStr})`;
                    })()}
                  >
                    <DashedTag
                      label={(() => {
                        const lastMessage = messages[messages.length - 1];
                        const isThinking =
                          lastMessage &&
                          lastMessage.role !== 'chariot' &&
                          lastMessage.role !== 'planner-output';

                        if (!isThinking && !isWaitingForResponse) {
                          return 'READY';
                        }

                        const recentMessages = messages.slice(-10);
                        const lastToolCall = recentMessages
                          .filter(msg => msg.role === 'tool call')
                          .pop();
                        const lastToolResponse = recentMessages
                          .filter(msg => msg.role === 'tool response')
                          .pop();

                        const isExecuting =
                          lastToolCall &&
                          (!lastToolResponse ||
                            new Date(lastToolCall.timestamp || '').getTime() >
                              new Date(
                                lastToolResponse.timestamp || ''
                              ).getTime());

                        if (isExecuting) {
                          return 'EXECUTING';
                        }

                        if (lastToolResponse) {
                          return 'ANALYZING';
                        }

                        return 'THINKING';
                      })()}
                      active={false}
                      size="xs"
                      className="font-normal"
                      icon={
                        <div
                          className={`size-2 rounded-full ${
                            isWaitingForResponse ||
                            (messages.length > 0 &&
                              messages[messages.length - 1]?.role !== 'chariot')
                              ? 'animate-pulse bg-green-500'
                              : 'bg-chariot-text-secondary'
                          }`}
                        />
                      }
                    />
                  </div>
                </div>

                {/* Workflow Status - only show when there's a trace_id */}
                {selectedConversation?.trace_id && (
                  <div className="border-chariot-stroke flex h-full items-center gap-3 border-l px-6">
                    <span className="text-chariot-text-primary text-sm font-medium">
                      Workflow:
                    </span>
                    <button
                      onClick={handleTracesCommand}
                      className="hover:text-chariot-text-primary flex cursor-pointer items-center gap-1.5"
                      title="Click to view workflow details"
                    >
                      {(() => {
                        // No traces or empty
                        if (
                          !traces ||
                          !Array.isArray(traces) ||
                          traces.length === 0
                        ) {
                          return (
                            <>
                              <div className="border-chariot-stroke size-2 rounded-full border-2" />
                              <span className="text-chariot-text-secondary text-sm">
                                Waiting
                              </span>
                            </>
                          );
                        }

                        // Check workflow status
                        const spanStatus = new Map<
                          string,
                          { hasStart: boolean; hasEnd: boolean }
                        >();
                        traces.forEach(event => {
                          if (!event) return;
                          const isTrackedEvent =
                            event.event_type === 'agent_start' ||
                            event.event_type === 'agent_end' ||
                            event.event_type === 'job_start' ||
                            event.event_type === 'job_end' ||
                            event.event_type === 'job_queued';
                          if (!isTrackedEvent) return;

                          const spanId = event.span_id;
                          const current = spanStatus.get(spanId) || {
                            hasStart: false,
                            hasEnd: false,
                          };
                          if (
                            event.event_type === 'agent_start' ||
                            event.event_type === 'job_start' ||
                            event.event_type === 'job_queued'
                          ) {
                            current.hasStart = true;
                          }
                          if (
                            event.event_type === 'agent_end' ||
                            event.event_type === 'job_end'
                          ) {
                            current.hasEnd = true;
                          }
                          spanStatus.set(spanId, current);
                        });

                        const hasTrackableSpans = spanStatus.size > 0;
                        const allSpansComplete = Array.from(
                          spanStatus.values()
                        ).every(status => !status.hasStart || status.hasEnd);
                        const isComplete =
                          hasTrackableSpans && allSpansComplete;

                        if (!hasTrackableSpans) {
                          return (
                            <>
                              <div className="border-chariot-stroke size-2 rounded-full border-2" />
                              <span className="text-chariot-text-secondary text-sm">
                                Waiting
                              </span>
                            </>
                          );
                        }

                        if (isComplete) {
                          return (
                            <>
                              <span className="text-green-500">✓</span>
                              <span className="text-chariot-text-secondary text-sm">
                                Complete
                              </span>
                            </>
                          );
                        }

                        return (
                          <>
                            <div className="size-2 animate-pulse rounded-full bg-blue-500" />
                            <span className="text-chariot-text-secondary text-sm">
                              Running
                            </span>
                          </>
                        );
                      })()}
                    </button>
                  </div>
                )}

                {/* Subagents button - show when conversation has children */}
                {childConversations.length > 0 && (
                  <div className="border-chariot-stroke flex h-full items-center gap-2 border-l px-6">
                    <button
                      onClick={() => {
                        const { openTab } = useTraceDrawerStore.getState();
                        openTab('subagents', selectedConversation?.trace_id);
                      }}
                      className="text-chariot-text-secondary hover:text-chariot-text-primary flex items-center gap-1.5 text-sm"
                      title="View subagent conversations"
                    >
                      Subagents
                      <span className="bg-chariot-background-secondary rounded-full px-1.5 text-[10px] font-medium">
                        {childConversations.length}
                      </span>
                      {childConversations.some(c => c.status === 'active') && (
                        <span className="relative flex size-2">
                          <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Subagent banner - persists above messages when viewing a subagent */}
          {isSubagent && parentConversationId && (
            <div className="border-chariot-stroke bg-chariot-brand-primary/5 border-b">
              <div className="flex items-center gap-2 px-4 py-2">
                <button
                  onClick={() => {
                    setSearchParams(prev => {
                      const newParams = new URLSearchParams(prev);
                      newParams.set('conversationId', parentConversationId);
                      return newParams;
                    });
                  }}
                  className="text-chariot-brand-primary hover:text-chariot-brand-primary/80 flex items-center gap-1 text-sm font-medium transition-colors"
                >
                  <ChevronLeftIcon className="size-4" />
                  Return to parent
                </button>
                <span className="text-chariot-text-tertiary text-xs">
                  Viewing subagent
                  {selectedConversation?.topic
                    ? `: ${selectedConversation.topic}`
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* Scroll to bottom button */}
          {showScrollToBottom && (
            <button
              onClick={() => {
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollTop =
                    scrollContainerRef.current.scrollHeight;
                }
              }}
              className="bg-chariot-background-primary border-chariot-stroke hover:border-chariot-brand-primary hover:bg-chariot-background-secondary absolute bottom-16 left-1/2 z-10 flex size-8 -translate-x-1/2 items-center justify-center rounded-full border transition-colors"
              title="Scroll to bottom"
            >
              <ArrowDownIcon className="text-chariot-text-secondary size-4" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-x-hidden overflow-y-auto"
          >
            {/* Show welcome screen only when truly starting fresh */}
            {!conversationId ? (
              <div key="welcome" className="h-full">
                <ChatWelcome
                  onExampleClick={handleExampleClick}
                  mode={mode}
                  autoFocus={!isInputDisabled}
                  canUseAgentMode={canUseAgentMode}
                  onModeChange={setMode}
                  agents={agents}
                  disabled={isAIDisabledButBypassed}
                  onSendMessage={async message => {
                    if (isInputDisabled) return;

                    try {
                      // Check for mode switch commands (Praetorian users only)
                      if (canUseAgentMode) {
                        if (message === '/agent') {
                          setMode('agent');
                          setNewMessage('');
                          return;
                        }
                        if (message === '/query') {
                          setMode('query');
                          setNewMessage('');
                          return;
                        }
                      }

                      // createConversationWorker sends the message to /planner and returns the conversation
                      const newConversation = await createConversationWorker(
                        message,
                        mode
                      );
                      const newId =
                        newConversation.uuid ||
                        newConversation.id ||
                        newConversation.key?.replace('#conversation#', '');
                      if (newId) {
                        setSearchParams(prev => {
                          const newParams = new URLSearchParams(prev);
                          newParams.set('conversationId', newId);
                          return newParams;
                        });

                        // The worker will start polling and fetch messages automatically
                        // No need to manually add pending message since createConversation
                        // sends the message directly and it will appear in the first poll
                      }
                      setNewMessage('');
                    } catch (error) {
                      console.error('Failed to send message:', message, error);
                      setFailedMessage(message);
                    }
                  }}
                  newMessage={newMessage}
                  setNewMessage={setNewMessage}
                />
              </div>
            ) : (
              <div
                key={conversationId || 'conversation'}
                className="flex min-h-full flex-col justify-end"
              >
                <div className="px-6 pt-4">
                  {/* Show loading indicator when conversation is loading with no messages */}
                  {isLoadingConversation && messages.length === 0 ? (
                    <div className="mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-chariot-brand-primary/10 -ml-1.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full">
                          <div className="bg-chariot-brand-primary h-2 w-2 rounded-full"></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <div className="bg-chariot-text-secondary h-2 w-2 animate-bounce rounded-full"></div>
                            <div
                              className="bg-chariot-text-secondary h-2 w-2 animate-bounce rounded-full"
                              style={{ animationDelay: '150ms' }}
                            ></div>
                            <div
                              className="bg-chariot-text-secondary h-2 w-2 animate-bounce rounded-full"
                              style={{ animationDelay: '300ms' }}
                            ></div>
                          </div>
                          <span className="text-chariot-text-secondary text-sm">
                            Loading conversation...
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(() => {
                        // Group tool calls with their responses
                        const filteredMessages = messages.filter(message => {
                          if (message.role !== 'planner-output') {
                            return true;
                          }

                          return (
                            Array.isArray(message.attachments) &&
                            message.attachments.length > 0
                          );
                        });
                        const grouped: Array<{
                          type: 'message' | 'tool-pair';
                          data: any;
                        }> = [];

                        for (let i = 0; i < filteredMessages.length; i++) {
                          const msg = filteredMessages[i];

                          if (msg.role === 'tool call') {
                            const toolUseId = (msg as any).toolUseId;
                            // Find matching response
                            const response = filteredMessages.find(
                              (m, _idx) =>
                                _idx > i &&
                                m.role === 'tool response' &&
                                (m as any).toolUseId === toolUseId
                            );

                            if (response) {
                              grouped.push({
                                type: 'tool-pair',
                                data: { call: msg, response },
                              });
                            } else {
                              // No response yet, show just the call
                              grouped.push({
                                type: 'tool-pair',
                                data: { call: msg, response: null },
                              });
                            }
                          } else if (msg.role === 'tool response') {
                            // Skip - already paired with call
                            const toolUseId = (msg as any).toolUseId;
                            const alreadyPaired = grouped.some(
                              g =>
                                g.type === 'tool-pair' &&
                                (g.data.response as any)?.toolUseId ===
                                  toolUseId
                            );
                            if (!alreadyPaired) {
                              // Orphaned response, show it anyway
                              grouped.push({
                                type: 'tool-pair',
                                data: { call: null, response: msg },
                              });
                            }
                          } else {
                            grouped.push({ type: 'message', data: msg });
                          }
                        }

                        return grouped.map((item, _idx) => {
                          if (item.type === 'message') {
                            // Show agent execution card before chariot response if agent was active
                            const isChariotResponse =
                              item.data.role === 'chariot';
                            const hasAgentExecution =
                              conversationId &&
                              agentExecutions.has(conversationId);

                            return (
                              <div key={item.data.id || item.data.key}>
                                {isChariotResponse && hasAgentExecution && (
                                  <div className="mb-4">
                                    <AgentExecutionCard
                                      agentName={
                                        agentExecutions.get(conversationId!)!
                                          .agentName
                                      }
                                      agentTitle={
                                        agentExecutions.get(conversationId!)!
                                          .agentTitle
                                      }
                                      status={
                                        agentExecutions.get(conversationId!)!
                                          .status
                                      }
                                      toolCount={
                                        agentExecutions.get(conversationId!)!
                                          .toolCount
                                      }
                                      tokenCount={
                                        agentExecutions.get(conversationId!)!
                                          .tokenCount
                                      }
                                      isExpanded={expandedAgentCards.has(
                                        conversationId!
                                      )}
                                      onToggle={() =>
                                        toggleAgentCard(conversationId!)
                                      }
                                      toolCalls={
                                        agentExecutions.get(conversationId!)!
                                          .toolCalls
                                      }
                                    />
                                  </div>
                                )}
                                <ChatMessage message={item.data} />
                              </div>
                            );
                          } else {
                            // For tool pairs, always pass the call as the message
                            if (item.data.call) {
                              return (
                                <ChatMessage
                                  key={item.data.call.id || item.data.call.key}
                                  message={item.data.call}
                                  pairedResponse={item.data.response}
                                />
                              );
                            } else if (item.data.response) {
                              // Orphaned response - show as regular message
                              return (
                                <ChatMessage
                                  key={
                                    item.data.response.id ||
                                    item.data.response.key
                                  }
                                  message={item.data.response}
                                />
                              );
                            }
                            return null;
                          }
                        });
                      })()}

                      {/* Show all pending messages */}
                      {workerPendingMessages.map(pending => (
                        <div key={pending.id} className="mb-4">
                          <div className="flex items-start gap-3">
                            <div
                              className="bg-chariot-stroke mt-1 w-1 flex-shrink-0 rounded-full"
                              style={{ height: '1.2em' }}
                            ></div>
                            <div className="text-chariot-text-secondary text-[15px] leading-[1.6] font-medium break-words whitespace-pre-wrap">
                              {pending.text}
                              <span className="ml-2 text-xs opacity-50">
                                (sending...)
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Show failed message */}
                      {failedMessage && (
                        <div className="mb-4">
                          <div className="text-chariot-text-secondary text-[15px] leading-[1.6] font-medium break-words whitespace-pre-wrap opacity-50">
                            {failedMessage}
                          </div>
                          <div className="text-chariot-text-red text-xs">
                            API Error - Server is temporarily unavailable
                          </div>
                        </div>
                      )}

                      {(() => {
                        const lastMessage = messages[messages.length - 1];
                        const isThinking =
                          lastMessage &&
                          lastMessage.role !== 'chariot' &&
                          lastMessage.role !== 'planner-output';

                        return (
                          isThinking && (
                            <div className="mb-4">
                              <div className="flex items-center gap-3">
                                <div className="bg-chariot-brand-primary/10 -ml-1.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full">
                                  <div className="bg-chariot-brand-primary h-2 w-2 rounded-full"></div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <div className="bg-chariot-text-secondary h-2 w-2 animate-bounce rounded-full"></div>
                                    <div
                                      className="bg-chariot-text-secondary h-2 w-2 animate-bounce rounded-full"
                                      style={{ animationDelay: '150ms' }}
                                    ></div>
                                    <div
                                      className="bg-chariot-text-secondary h-2 w-2 animate-bounce rounded-full"
                                      style={{ animationDelay: '300ms' }}
                                    ></div>
                                  </div>
                                  {loadingStatus && (
                                    <span className="text-chariot-text-secondary text-sm">
                                      {loadingStatus}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        );
                      })()}

                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Warning banner when AI inference is disabled but user bypasses lock */}
          {isAIDisabledButBypassed && conversationId && (
            <div className="mx-6 mb-2 flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-2.5">
              <ExclamationTriangleIcon className="size-5 shrink-0 text-amber-500" />
              <p className="text-chariot-text-secondary text-sm">
                LLM functionality disabled. Administrators can enable AI
                Inference under Settings {'->'} Organization.
              </p>
            </div>
          )}

          {/* Only show bottom input when in conversation */}
          {conversationId && (
            <div className="px-6 pb-6">
              <form onSubmit={handleSendMessage} className="relative">
                <div
                  onClick={() => textareaRef.current?.focus()}
                  className={`bg-chariot-background-primary flex cursor-text items-center gap-3 rounded-full border px-4 py-3 transition-colors ${
                    mode === 'agent'
                      ? 'border-chariot-brand-primary hover:border-chariot-brand-primary focus-within:border-chariot-brand-primary'
                      : 'border-chariot-stroke hover:border-chariot-brand-primary focus-within:border-chariot-brand-primary'
                  }`}
                >
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setShowTipsMenu(prev => !prev);
                    }}
                    className="tips-menu-button text-chariot-text-secondary hover:text-chariot-text-primary flex-shrink-0 transition-colors"
                    title="Show search shortcuts"
                  >
                    <LightBulbIcon className="size-5" />
                  </button>
                  {canUseAgentMode && !isAIDisabledButBypassed && (
                    <ModeIndicator
                      mode={mode}
                      onModeChange={setMode}
                      disabled={isWaitingForResponse}
                    />
                  )}
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    disabled={isInputDisabled}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      } else if (e.key === 'ArrowUp') {
                        const textarea = e.currentTarget;
                        const textBeforeCursor = textarea.value.substring(
                          0,
                          textarea.selectionStart
                        );
                        const isOnFirstLine = !textBeforeCursor.includes('\n');
                        if (isOnFirstLine && messageHistory.length > 0) {
                          e.preventDefault();
                          if (historyIndex === -1) {
                            setSavedMessage(newMessage);
                          }
                          const newIndex =
                            historyIndex === -1
                              ? messageHistory.length - 1
                              : Math.max(0, historyIndex - 1);
                          setHistoryIndex(newIndex);
                          setNewMessage(messageHistory[newIndex]);
                        }
                      } else if (e.key === 'ArrowDown') {
                        if (historyIndex >= 0) {
                          const textarea = e.currentTarget;
                          const textAfterCursor = textarea.value.substring(
                            textarea.selectionStart
                          );
                          const isOnLastLine = !textAfterCursor.includes('\n');
                          if (isOnLastLine) {
                            e.preventDefault();
                            const newIndex = historyIndex + 1;
                            if (newIndex >= messageHistory.length) {
                              setHistoryIndex(-1);
                              setNewMessage(savedMessage);
                              setSavedMessage('');
                            } else {
                              setHistoryIndex(newIndex);
                              setNewMessage(messageHistory[newIndex]);
                            }
                          }
                        }
                      }
                    }}
                    placeholder={
                      isSubagentFinished
                        ? 'This subagent conversation has completed'
                        : isSubagentQueued
                          ? 'Subagent is starting up'
                          : isSubagent
                            ? 'Provide subagent guidance'
                            : 'Ask anything about your attack surface'
                    }
                    className="text-chariot-text-primary placeholder-chariot-text-secondary flex-1 resize-none bg-transparent text-sm focus:outline-hidden"
                    rows={1}
                    style={{
                      minHeight: '24px',
                      maxHeight: '120px',
                    }}
                  />
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      toggleRecording();
                    }}
                    className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                      isRecording
                        ? 'animate-pulse bg-red-500 text-white hover:bg-red-600'
                        : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                    }`}
                    title={isRecording ? 'Stop recording' : 'Voice input'}
                  >
                    <MicrophoneIcon className="size-4" />
                  </button>
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || isInputDisabled}
                    onClick={e => e.stopPropagation()}
                    className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                      mode === 'agent'
                        ? 'bg-chariot-status-high hover:bg-chariot-status-high/90'
                        : 'bg-chariot-brand-primary hover:bg-chariot-brand-primary/90'
                    }`}
                  >
                    <ArrowUpIcon className="size-4" />
                  </button>
                </div>

                {/* Command/Search menu - show above input (since input is at bottom of screen) */}
                {showCommandMenu &&
                  filteredCommands.length > 0 &&
                  (menuType === 'search' || canUseAgentMode) && (
                    <div
                      ref={commandMenuRef}
                      className="command-menu bg-chariot-background-primary border-chariot-stroke absolute right-0 bottom-full left-0 z-50 mb-2 max-h-80 overflow-hidden rounded-lg border shadow-lg"
                    >
                      <div className="max-h-80 overflow-y-auto p-2">
                        {filteredCommands.map((cmd, index) => (
                          <button
                            key={`${cmd.command}-${index}`}
                            type="button"
                            onClick={() => {
                              // For entity search (@ or #), replace with JSON data if available
                              if (menuType === 'search' && cmd.data) {
                                setNewMessage(
                                  replaceCurrentWord(cmd.command, cmd.data)
                                );
                                textareaRef.current?.focus();
                              }
                              // For search without data, just replace text
                              else if (menuType === 'search') {
                                setNewMessage(replaceCurrentWord(cmd.command));
                                textareaRef.current?.focus();
                              }
                              // For commands, execute action
                              else {
                                selectCommand(cmd.command);
                              }
                            }}
                            className="hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left transition-colors"
                          >
                            <div className="text-chariot-text-primary text-sm font-medium">
                              {cmd.command}
                            </div>
                            <div className="text-chariot-text-secondary text-xs">
                              {cmd.description}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Tips menu popover */}
                <TipsMenu
                  show={showTipsMenu}
                  position="above"
                  onTipClick={icon => {
                    setNewMessage(
                      newMessage +
                        (newMessage && !newMessage.endsWith(' ') ? ' ' : '') +
                        icon
                    );
                    textareaRef.current?.focus();
                    setShowTipsMenu(false);
                  }}
                />
              </form>
            </div>
          )}

          {/* Trace Drawer - positioned within the main chat area container */}
          <TraceDrawer
            width="320px"
            childConversations={childConversations}
            currentConversationId={conversationId ?? undefined}
            parentConversationId={isSubagent ? parentConversationId : undefined}
            onNavigateToConversation={id => {
              setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.set('conversationId', id);
                return newParams;
              });
            }}
          />
        </div>
      </FeatureLock>
    </div>
  );
};

export default AIAssistant;
